import {Component, computed, inject, input, signal} from '@angular/core';
import {ChartData, ChartOptions} from 'chart.js';
import {SubCriteriaAttributeItem} from '../../subcriteria-attribute-evaluation/subcriteria-attribute-evaluation.types';
import {ProgressDashboardChartContainerComponent} from '../progress-dashboard-chart-container/progress-dashboard-chart-container.component';
import {EvaluationSubCriteriaImportanceScore} from '@frontend/shared';
import {TranslocoService} from '@jsverse/transloco';

export const IMPORTANCE_SCORE_COLORS: Record<EvaluationSubCriteriaImportanceScore, string> = {
  [EvaluationSubCriteriaImportanceScore.BASIC_ATTRIBUTE]: '#AABAE7',
  [EvaluationSubCriteriaImportanceScore.PERFORMANCE_ATTRIBUTE]: '#8AA3EB',
  [EvaluationSubCriteriaImportanceScore.ENTHUSIASM_ATTRIBUTE]: '#6785E6',
  [EvaluationSubCriteriaImportanceScore.INDIFFERENT_ATTRIBUTE]: '#4C6FE1',
  [EvaluationSubCriteriaImportanceScore.NOT_RELEVANT]: '#2953DB',
};

const IMPORTANCE_SCORE_ORDER: string[] = Object.keys(IMPORTANCE_SCORE_COLORS);

@Component({
  selector: 'app-progress-dashboard-pie-chart-container',
  imports: [ProgressDashboardChartContainerComponent],
  templateUrl: './progress-dashboard-pie-chart.component.html',
  styleUrls: ['./progress-dashboard-pie-chart.component.scss'],
})
export class ProgressDashboardPieChartComponent {
  readonly label = input.required<string>();
  readonly subCriteriaAttributes = input.required<SubCriteriaAttributeItem[]>();
  private readonly translocoService = inject(TranslocoService);

  readonly data = computed<ChartData<'pie'> | null>(() => {
    const ratingDistribution = this.calcRatingDistribution();
    return {
      labels: Object.keys(ratingDistribution).map((score) =>
        this.translocoService.translate('progress.dashboard.criteria.details.table.' + score),
      ),
      datasets: [
        {
          data: Object.values(ratingDistribution),
          backgroundColor: Object.keys(ratingDistribution).map((key) => this.getColorForKey(key)),
        },
      ],
    };
  });

  protected options = signal<ChartOptions>({
    layout: {
      padding: {
        left: 0, // adds space between the left legend and the pie
      },
    },
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'left',
        align: 'center',
        labels: {
          textAlign: 'left',
          padding: 12,
          boxWidth: 14,
          boxHeight: 14,
          color: 'white',
          font: {size: 12},
          usePointStyle: false,
        },
      },
    },
  });

  protected calcRatingDistribution(): Record<string, number> {
    const scores = this.subCriteriaAttributes()
      .filter((attribute) => attribute?.selectedImportanceScore !== undefined)
      .map(
        (attribute) => attribute.selectedImportanceScore as EvaluationSubCriteriaImportanceScore,
      );

    // Count occurrences of each score
    const scoreCounts: Record<string, number> = {};
    scores.forEach((score) => {
      scoreCounts[score] = (scoreCounts[score] || 0) + 1;
    });

    // Calculate percentages
    const scorePercentages: Record<string, number> = {} as any;

    Object.entries(scoreCounts)
      .sort(([a], [b]) => {
        const ia = IMPORTANCE_SCORE_ORDER.indexOf(a);
        const ib = IMPORTANCE_SCORE_ORDER.indexOf(b);
        if (ia !== -1 && ib !== -1) return ia - ib;
        if (ia !== -1) return -1;
        if (ib !== -1) return 1;
        return a.localeCompare(b);
      })
      .forEach(([score, count]) => {
        scorePercentages[score as string] = count;
      });
    return scorePercentages;
  }

  private readonly fallbackColors = ['#AABAE7', '#8AA3EB', '#6785E6', '#4C6FE1', '#2953DB'];

  private getColorForKey(key: string): string {
    if (key in IMPORTANCE_SCORE_COLORS) {
      return IMPORTANCE_SCORE_COLORS[key as EvaluationSubCriteriaImportanceScore];
    }

    let hash = 0;
    for (let i = 0; i < key.length; i++) {
      hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
    }
    return this.fallbackColors[hash % this.fallbackColors.length];
  }
}
